/**
 * [zgłoszenie właściciela 2026-09-17, obszar 3] MASKA DD.MM.RRRR I TWARDE GRANICE LAT 1910–2099.
 *
 * Właściciel: „w time i data picker jest opcja wpisania daty, ustaw limit nie większy niż 1910 i 2099
 * oraz od razu kropkuj wpisaną datę, przyjmujemy zapis DD.MM.RRRR".
 *
 * CO BYŁO ŹLE — i to nie była niewygoda, tylko zapisana bzdura:
 *  1. Pole „Wpisz datę" było GOŁYM polem tekstowym (`maxLength={10}`, bez maski). Człowiek wpisujący
 *     datę z klawiatury musiał sam stawiać kropki, a gdy ich nie postawił, pole przyjmowało sam ciąg
 *     cyfr. Na zrzucie właściciela stoi w nim `2312342342`.
 *  2. Wartość szła do formularza PO KAŻDYM naciśnięciu klawisza, a parser przyjmował osiem cyfr bez
 *     separatorów jako DDMMRRRR. Ciąg z punktu 1 przy ÓSMEJ cyfrze („23123423") był więc poprawną
 *     datą 23.12.**3423** i w tej chwili zapisał się do pola „Obowiązuje do". Dalsze cyfry („42")
 *     psuły już tylko tekst w polu — wartość formularza została z rokiem 3423. Dlatego karta pokazywała
 *     „23 grudnia 3423" i NIC tego nie zgłosiło.
 *  3. Rok spoza jakiegokolwiek sensownego zakresu nie był sprawdzany na ŻADNEJ drodze wejścia:
 *     ani we wpisie, ani na liście lat (`zakresLat` rozciągała listę do oglądanego roku, więc dla
 *     3423 miała ~1500 pozycji), ani na skokach ±10 lat (mogły zejść do roku 1000 i wyżej niż 9999).
 *
 * TEN PLIK jest jednym źródłem trzech rzeczy dla obu pickerów (DatePicker, DateTimePicker) i ich
 * wspólnego nagłówka:
 *  - maski DD.MM.RRRR (kropki wstawiają się same, także po wklejeniu, z zachowaniem pozycji kursora),
 *  - granic 1910–2099 jako DAT (`DATA_DOLNA`/`DATA_GORNA`), przez które przechodzi min/max pola —
 *    węższe wygrywa, więc `min` strony nigdy nie poszerza zakresu, a granica nigdy go nie zawęża
 *    bardziej, niż chce strona,
 *  - dociągania OGLĄDANEGO miesiąca do granic, żeby wartość z rokiem spoza zakresu (taka jak ta ze
 *    zrzutu, już zapisana w bazie) nie wyrzucała kalendarza w rok 3423.
 */

/** Najstarszy dopuszczalny rok — polecenie właściciela. */
export const ROK_DOLNY = 1910;
/** Najmłodszy dopuszczalny rok — polecenie właściciela. */
export const ROK_GORNY = 2099;

export const DATA_DOLNA = `${ROK_DOLNY}-01-01`;
export const DATA_GORNA = `${ROK_GORNY}-12-31`;

/** Komunikat dla człowieka przy ekranie: rok poza granicami NIE zapisuje się po cichu. */
export const KOMUNIKAT_ROK = `Rok musi być z przedziału ${ROK_DOLNY}–${ROK_GORNY}.`;
/** Komunikat dla daty odrzuconej przez min/max samego pola (zakres węższy niż granice). */
export const KOMUNIKAT_ZAKRES = "Ta data jest poza dozwolonym zakresem pola.";

const cyfra = (znak) => znak >= "0" && znak <= "9";

/** Ile cyfr łącznie po zapełnieniu kolejnych grup maski: dzień (2), miesiąc (4), rok (8). */
const KONIEC_GRUPY = [2, 4, 8];

/** „RRRR-MM-DD" z dowolnej postaci ISO (także z godziną); null, gdy to nie jest data. */
export function dzienISO(wartosc) {
    const dop = typeof wartosc === "string" ? wartosc.match(/^\d{4}-\d{2}-\d{2}/) : null;
    return dop ? dop[0] : null;
}

/** Rok z „RRRR-MM-DD" (także z godziną) jako liczba; null, gdy to nie jest data. */
export function rokZISO(wartosc) {
    const dzien = dzienISO(wartosc);
    return dzien ? Number(dzien.slice(0, 4)) : null;
}

/** Czy rok mieści się w granicach z polecenia właściciela. */
export function rokWZakresie(rok) {
    return Number.isFinite(rok) && rok >= ROK_DOLNY && rok <= ROK_GORNY;
}

/**
 * Skuteczne granice pola: przecięcie min/max podanych przez stronę z granicami 1910–2099.
 * WĘŻSZE WYGRYWA w obie strony — `min="1800-01-01"` nie otworzy roku 1800, a brak min/max nie
 * otwiera całej osi czasu. Zapis min > max (np. „widoczne do" przy publikacji zaczynającej się
 * w 2999) zostaje jako pusty zakres: nic nie da się wybrać, dokładnie jak przed tą zmianą.
 */
export function granice(min, max) {
    const odStrony = dzienISO(min);
    const doStrony = dzienISO(max);
    return {
        min: odStrony && odStrony > DATA_DOLNA ? odStrony : DATA_DOLNA,
        max: doStrony && doStrony < DATA_GORNA ? doStrony : DATA_GORNA,
    };
}

/**
 * Oglądany miesiąc dociągnięty do granic — MIESIĄC ZOSTAJE, zmienia się tylko rok.
 * Potrzebne dla wartości, która już siedzi w bazie z rokiem 3423 (zrzut właściciela): kalendarz
 * ma się otworzyć na krawędzi zakresu, a nie w roku, którego lista lat nawet nie zna.
 */
export function ograniczRokWidoku(data) {
    if (!(data instanceof Date) || isNaN(data.getTime())) return new Date();
    const rok = data.getFullYear();
    if (rok < ROK_DOLNY) return new Date(ROK_DOLNY, data.getMonth(), 1);
    if (rok > ROK_GORNY) return new Date(ROK_GORNY, data.getMonth(), 1);
    return data;
}

/** Grupy cyfr z tekstu + informacja, czy po grupie stał separator (czyli czy człowiek ją ZAMKNĄŁ). */
function grupyCyfr(tekst) {
    const grupy = [];
    let biezaca = "";
    for (const znak of tekst) {
        if (cyfra(znak)) { biezaca += znak; continue; }
        if (biezaca !== "") { grupy.push({ cyfry: biezaca, zamknieta: true }); biezaca = ""; }
    }
    if (biezaca !== "") grupy.push({ cyfry: biezaca, zamknieta: false });
    return grupy;
}

/**
 * Surowy tekst pola → co najwyżej osiem cyfr DDMMRRRR.
 *
 * `dopelniajGrupy` (dokładamy tylko wtedy, gdy kursor stoi na KOŃCU, czyli człowiek DOPISUJE):
 * separator po jednej cyfrze dnia albo miesiąca znaczy „ta grupa jest skończona", więc „1.3.1985"
 * to 01.03.1985, a nie 13.19.85. Przy edycji w ŚRODKU dopełniania nie ma — inaczej skasowanie jednej
 * cyfry miesiąca („23.1.2026" po backspace) samo dorobiłoby zero i podmieniło miesiąc na inny.
 *
 * Nadmiar grupy PRZELEWA SIĘ do następnej, dzięki czemu skasowanie samej kropki („23.122026")
 * nie gubi roku.
 */
export function cyfryDaty(surowy, dopelniajGrupy = false) {
    const tekst = String(surowy == null ? "" : surowy);
    let cyfry = "";
    let grupa = 0;
    for (const czesc of grupyCyfr(tekst)) {
        if (grupa >= KONIEC_GRUPY.length) break;
        let reszta = czesc.cyfry;
        if (dopelniajGrupy && czesc.zamknieta && grupa < 2 && reszta.length === 1) reszta = `0${reszta}`;
        while (reszta.length > 0 && grupa < KONIEC_GRUPY.length) {
            const wolne = KONIEC_GRUPY[grupa] - cyfry.length;
            cyfry += reszta.slice(0, wolne);
            reszta = reszta.slice(wolne);
            if (cyfry.length >= KONIEC_GRUPY[grupa]) grupa += 1;
        }
    }
    return cyfry;
}

/** Osiem cyfr → „DD.MM.RRRR"; krócej → tyle, ile jest, z kropkami na swoich miejscach. */
export function formatujZCyfr(cyfry) {
    const c = String(cyfry == null ? "" : cyfry).slice(0, 8);
    let wynik = c.slice(0, 2);
    if (c.length > 2) wynik += `.${c.slice(2, 4)}`;
    if (c.length > 4) wynik += `.${c.slice(4, 8)}`;
    return wynik;
}

/** Pozycja PO `ile`-tej cyfrze w tekście — most między „która cyfra" a „który znak". */
function poCyfrach(tekst, ile) {
    if (ile <= 0) return 0;
    let licznik = 0;
    for (let i = 0; i < tekst.length; i += 1) {
        if (cyfra(tekst[i])) {
            licznik += 1;
            if (licznik === ile) return i + 1;
        }
    }
    return tekst.length;
}

/**
 * MASKA: surowy tekst pola (po klawiszu albo wklejeniu) + pozycja kursora → tekst z kropkami
 * i pozycja kursora PO TEJ SAMEJ CYFRZE, co przed maskowaniem.
 *
 * ⚠ Liczymy CYFRY przed kursorem, nie znaki. To jest cała odpowiedź na najczęstszą wadę takich
 * masek: przy przepisywaniu wartości pola kursor skacze na koniec, bo kod zna tylko nową treść.
 * Cyfra jest niezmiennikiem — kropki wstawiamy i wyjmujemy, cyfra przed kursorem zostaje ta sama.
 */
export function przeliczMaske(surowy, kursorSurowy) {
    const tekst = String(surowy == null ? "" : surowy);
    const poz = Number.isInteger(kursorSurowy)
        ? Math.max(0, Math.min(kursorSurowy, tekst.length))
        : tekst.length;
    const naKoncu = poz >= tekst.length;
    const wynik = formatujZCyfr(cyfryDaty(tekst, naKoncu));
    if (naKoncu) return { tekst: wynik, kursor: wynik.length };
    const cyfrPrzed = (tekst.slice(0, poz).match(/\d/g) || []).length;
    return { tekst: wynik, kursor: poCyfrach(wynik, cyfrPrzed) };
}

/**
 * Backspace stojący za kropką: kropki nie ma sensu kasować, bo maska wstawi ją z powrotem —
 * kasujemy CYFRĘ przed nią. Bez tego kursor „zacinał się" na kropce: pierwsze kasowanie nie
 * zmieniało nic widocznego.
 * Oddaje `null`, gdy zwykłe kasowanie jest w porządku (przed kursorem stoi cyfra) albo gdy nie ma
 * już czego kasować.
 */
export function kasujWTyl(tekst, kursor) {
    const t = String(tekst == null ? "" : tekst);
    if (!Number.isInteger(kursor) || kursor <= 0 || kursor > t.length) return null;
    if (cyfra(t[kursor - 1])) return null;
    let i = kursor - 1;
    while (i > 0 && !cyfra(t[i - 1])) i -= 1;
    if (i === 0) return null;
    return przeliczMaske(t.slice(0, i - 1) + t.slice(kursor), i - 1);
}
